/**
 * View component for /admin/authoring/sites/:siteId
 *
 * Displays a single site from the 'byId' map in the site reducer
 * as defined by the 'selected' property
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// import actions
import * as siteActions from '../siteActions';
import * as siteLocationActions from '../../siteLocation/siteLocationActions';
import * as sponsorActions from '../../sponsor/sponsorActions';
import * as studyActions from '../../study/studyActions';
import * as studyUserActions from '../../studyUser/studyUserActions';
import * as studyRoleActions from '../../studyRole/studyRoleActions';

// import global components
import AlertModal from '../../../global/components/modals/AlertModal.js.jsx';
import AuthoringLayout from '../../admin/components/AuthoringLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';
import Pagination from "../../../global/components/helpers/Pagination.js.jsx";
import { FileInput, SearchInput } from '../../../global/components/forms';

// import site components
import AdminStudyUserListItem from '../../studyUser/components/AdminStudyUserListItem.js.jsx';

// import site css modules
import siteStyles from '../siteModuleStyles.css';

// import utilities
import adminUtils from '../../../global/utils/adminUtils';
import filterUtils from '../../../global/utils/filterUtils';

// import third party libraries
import _ from 'lodash';

class AdminSingleSite extends Base {
  constructor(props) {
    super(props);
    this.state = {
      perPage: 50
      , queryText: this.props.query || ''
    };
    this._bind(
      '_handleBulkDeactivate'
      , '_handleQuery'
      , '_handleSetPagination'
      , '_handleSort'
      , '_handleUnlinkLocation'
      , '_resetPagination'
      , '_setPerPage'
    );
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    dispatch(siteActions.fetchSingleIfNeeded(match.params.siteId)).then(json => {
      dispatch(sponsorActions.fetchSingleIfNeeded(json.item._organization));
      dispatch(studyActions.fetchSingleIfNeeded(json.item._group));
      dispatch(siteLocationActions.fetchSingleIfNeeded(json.item._siteLocation))
      dispatch(studyUserActions.fetchListIfNeeded('_group', json.item._group ,'siteNumber', json.item.number)).then(json => {

      })
      dispatch(studyUserActions.setFilter({query: '', sortBy: 'email'}, "_group", json.item._group, 'siteNumber', json.item.number));
      dispatch(studyUserActions.setPagination({page: 1, per: 50}, "_group", json.item._group, 'siteNumber', json.item.number));
      dispatch(studyRoleActions.fetchListIfNeeded('study', json.item._group));
    });
  }

  _handleBulkDeactivate() {

    const { dispatch, match, siteStore } = this.props;
    const selectedSite = siteStore.selected.getItem();

    let prompt = confirm("Are you sure you want to deactivate ALL active users for this Site?")
    if(prompt) {
      dispatch(studyUserActions.sendBulkDeactivateViaSite(selectedSite._id)).then(response => {
        dispatch(studyUserActions.fetchList('_group', selectedSite._group ,'siteNumber', selectedSite.number))
      })
    }

  }

  _handleSort(sortBy) {
    const { dispatch, match, studyUserList } = this.props;
    let newFilter = studyUserList.filter;
    if(studyUserList.filter.sortBy && studyUserList.filter.sortBy.indexOf("-") < 0) {
      sortBy = "-" + sortBy;
    } else {
      sortBy = sortBy.substring(0)
    }
    newFilter.sortBy = sortBy;
    dispatch(studyUserActions.setFilter(newFilter, "_group", match.params.studyId));
  }

  _handleUnlinkLocation() {
    const { dispatch, match, siteStore } = this.props; 
    const selectedSite = siteStore.selected.getItem();
    let updatedSite = {...selectedSite};
    updatedSite._siteLocation = null;
    dispatch(siteActions.sendUpdateSite(updatedSite)).then(updateRes => {
      if(updateRes.success) {
        console.log("success")
        this.setState({confirmUnlink: false})
      } else {
        console.log('error')
      }
    })
  }

  _resetPagination() {
    const { dispatch, match } = this.props;
    dispatch(studyUserActions.setPagination({
      page: 1,
      per: parseInt(this.state.perPage)
    }, "_group", match.params.studyId));
  }

  _setPerPage(per) {
    const { dispatch, match, studyUserList } = this.props;
    let newPagination = studyUserList.pagination;
    newPagination.per = parseInt(per);
    newPagination.page = 1;
    dispatch(studyUserActions.setPagination(newPagination, "_group", match.params.studyId));
    this.setState({perPage: per});
  }

  _handleSetPagination(newPagination) {
    const { dispatch, match } = this.props;
    dispatch(studyUserActions.setPagination(newPagination, "_group", match.params.studyId));
  }

  _handleQuery(e) {
    const { dispatch, match, studyUserList } = this.props;
    // always defaulting the page to page 1 so we can see our results
    let pagination = {};
    pagination.page = 1;
    pagination.per = studyUserList.pagination.per;
    this._handleSetPagination(pagination);
    // continue query logic
    dispatch(studyUserActions.setFilter({
      query: e.target.value.toLowerCase()
      , sortBy: studyUserList.filter.sortBy
    }, "_group", match.params.studyId));
  }

  render() {
    const {
      location
      , match
      , paginatedList
      , siteStore
      , siteLocationStore
      , sortedList
      , sponsorStore
      , studyUserList
      , studyStore
      , studyUserStore
      , studyRoleStore
      , userStore
    } = this.props;

    /**
     * use the selected.getItem() utility to pull the actual site object from the map
     */
    const selectedSite = siteStore.selected.getItem();
    const selectedSponsor = sponsorStore.selected.getItem() || {};
    const selectedStudy = studyStore.selected.getItem() || {};
    const selectedSiteLocation = siteLocationStore.selected.getItem();

    const isEmpty = (
      !selectedSite
      || !selectedSite._id
      || siteStore.selected.didInvalidate
      // || !studyUserItems
      || !studyUserList
    );
    //
    //
    // let studyUserItems = [];
    // if(!isEmpty) {
    //   studyUserItems = studyUserStore.util.getList('_group', selectedSite._group,  'siteNumber', selectedSite.number);
    // }

    const sortedEmpty = sortedList.length === 0;

    return (
      <AuthoringLayout>
        <div className="flex">
          <Helmet
            title={"Site | Pro-ficiency"}
          />
          <section className="section">
            <div className="yt-container">
              <Breadcrumbs links={location.state.breadcrumbs} />
              { isEmpty ?
                (siteStore.isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
                :
                <div style={{ opacity: siteStore.isFetching ? 0.5 : 1 }}>
                  <div className="content-container">
                    <div className="yt-row space-between">
                      <p><strong>General site info</strong></p>
                      <div>
                        <button className="yt-btn danger x-small link" onClick={() => this._handleBulkDeactivate()}> Deactivate Site </button>

                        <Link className="yt-btn small" to={`${this.props.match.url}/update-site`}> Update Site </Link>
                      </div>
                    </div>
                    <hr/>

                    <h2>
                      <small><strong>Site name:</strong></small><br/>
                      { selectedSite.name }
                    </h2>

                    <small><strong>Site number:</strong></small>
                    <p>  {selectedSite.number}</p>
                    <small><strong>Sponsor:</strong> </small>
                    <p> {selectedSponsor.name ? selectedSponsor.name : ''}</p>
                    <small><strong>Study:</strong> </small>
                    <p> {selectedStudy.name ? selectedStudy.name : ''}</p>
                    <hr/>
                    <small><strong>Location info:</strong></small>
                    { selectedSite._siteLocation && selectedSiteLocation && selectedSiteLocation._id ?
                      <div>
                        <div className="u-pullRight">
                          <Link to={`/admin/site-locations/${selectedSiteLocation._id}/update`} className="yt-btn x-small">Edit location</Link>
                          <button onClick={() => this.setState({confirmUnlink: true})} className="yt-btn x-small danger link">Unlink </button>
                        </div>
                        <p>{selectedSiteLocation.name}</p>
                        <small><strong>Address:</strong></small>
                        <p>{selectedSiteLocation.address.street1}</p>
                        {selectedSiteLocation.address.street2 ?
                          <p>{selectedSiteLocation.address.street2}</p>
                          :
                          null 
                        }
                        <p>{selectedSiteLocation.address.city}, {selectedSiteLocation.address.state} {selectedSiteLocation.address.postal} {selectedSiteLocation.address.country}</p>
                        <small><strong>Description:</strong></small>
                        <p>{selectedSiteLocation.description}</p>
                      </div>
                      :
                      <div className="site-location-empty">
                        <div className="u-centerText">
                          <em>No location linked yet. </em>
                          <br/>
                          <br/>
                          <Link to={`${match.url}/link-location`} className="yt-btn small">Add site location</Link>
                        </div>
                      </div>
                    }
                  </div>
                  <div className="content-container">
                    <div className="yt-row space-between">
                      <p><strong>Site users</strong></p>
                    </div>
                    <div className="table-wrapper">
                      <table className="yt-table striped">
                        <caption>
                        </caption>
                        <thead>
                          <tr>
                            <th className="_20 sortable" onClick={()=> this._handleSort('email')}>
                              Email
                              {studyUserList.filter.sortBy === 'email' ?
                                <i className="ion ion-ios-arrow-down" />
                                : studyUserList.filter.sortBy === '-email' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                            <th className="_20 sortable" onClick={()=> this._handleSort('role')}>
                              Role
                              {studyUserList.filter.sortBy === 'role' ?
                                <i className="ion ion-ios-arrow-down" />
                                : studyUserList.filter.sortBy === '-role' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                            <th className="_10 sortable" onClick={()=> this._handleSort('siteNumber')}>
                              Site Number
                              {studyUserList.filter.sortBy === 'siteNumber' ?
                                <i className="ion ion-ios-arrow-down" />
                                : studyUserList.filter.sortBy === '-siteNumber' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                            <th className="_20 sortable" onClick={()=> this._handleSort('created')}>
                              Created
                              {studyUserList.filter.sortBy === 'created' ?
                                <i className="ion ion-ios-arrow-down" />
                                : studyUserList.filter.sortBy === '-created' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                            <th className="_10 sortable" onClick={()=> this._handleSort('status')}>
                              Status
                              {studyUserList.filter.sortBy === 'status' ?
                                <i className="ion ion-ios-arrow-down" />
                                : studyUserList.filter.sortBy === '-status' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                            <th className="_10">Onboarded?</th>
                            <th className="_10 sortable" onClick={()=> this._handleSort('admin')}>
                              Admin?
                              {studyUserList.filter.sortBy === 'admin' ?
                                <i className="ion ion-ios-arrow-down" />
                                : studyUserList.filter.sortBy === '-admin' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                            <th className="_20">
                              Last Activity
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {
                            paginatedList.map((studyUser, i) =>
                            <AdminStudyUserListItem
                              key={studyUser._id}
                              study={selectedStudy ? selectedStudy : {}}
                              studyUser={studyUser}
                              studyRole={studyRoleStore.byId[studyUser._groupRole]}
                            />
                          )
                        }
                      </tbody>
                    </table>
                    { sortedEmpty ?
                      null
                      :
                      <Pagination
                        totalPages={ Math.floor(sortedList.length / studyUserList.pagination.per) + 1}
                        pagination={studyUserList.pagination}
                        setPagination={this._handleSetPagination}
                      />
                    }
                  </div>
                  </div>

                </div>
              }
        </div>
        <AlertModal
          alertMessage="Are you sure you want to unlink this location?"
          alertTitle="Confirm unlink"
          closeAction={() => this.setState({confirmUnlink: false})}
          confirmAction={this._handleUnlinkLocation}
          confirmText="Yes, unlink"
          declineAction={() => this.setState({confirmUnlink: false})}
          declineText="Nevermind"
          isOpen={this.state.confirmUnlink}
          type="danger"
        />
      </section>
    </div>
      </AuthoringLayout>
    )
  }
}

AdminSingleSite.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  const loggedInUser = store.user.loggedIn.user;
  const selectedStudy = store.study.selected.getItem() || {};
  const selectedSponsor = store.sponsor.selected.getItem() || {};
  const selectedSite = store.site.selected.getItem() || {};
  const studyUserStore = store.studyUser;
  let studyUserList;

  if(
    selectedSite
    && selectedSite._id
    && selectedStudy
    && selectedStudy._id
    && store.studyUser.lists._group
    && store.studyUser.lists._group[selectedStudy._id]
    && store.studyUser.lists._group[selectedStudy._id].siteNumber
  ) {
    studyUserList = store.studyUser.lists._group[selectedStudy._id].siteNumber[selectedSite.number];
  }

  const userMap = store.user.byId;

  let sortedList = [];
  let paginatedList = [];
  let filteredByQuery = [];
  if (studyUserList) {

    const pagination = studyUserList.pagination;
    const filter = studyUserList.filter;
    const query = filter ? filter.query : '';
    const sortBy = filter ? filter.sortBy : 'email';

    // FILTER BY QUERY
    let queryTestString = ("" + query).toLowerCase().trim();
    // queryTestString = queryTestString.replace(/[^a-zA-Z0-9]/g,''); // replace all non-characters and numbers
    filteredByQuery = studyUserList && studyUserList.items ? studyUserList.items.filter((studyUserId) => {
      return filterUtils.filterStudyUser(queryTestString, studyUserStore.byId[studyUserId], userMap);
    }) : []

    // SORT THE LIST
    sortedList = filteredByQuery.map((item) => {
      let newItem = studyUserStore.byId[item];
      return newItem;
    });
    switch(sortBy) {
      case 'email':
        sortedList = _.orderBy(sortedList, [item => item.email.toLowerCase()], ['asc']);
        break;
      case '-email':
        sortedList = _.orderBy(sortedList, [item => item.email.toLowerCase()], ['desc']);
        break;
      case 'status':
        sortedList = _.orderBy(sortedList, [item => item.status.toLowerCase()], ['asc']);
        break;
      case '-status':
        sortedList = _.orderBy(sortedList, [item => item.status.toLowerCase()], ['desc']);
        break;
      case 'admin':
        sortedList = _.orderBy(sortedList, [item => item.admin], ['asc']);
        break;
      case '-admin':
        sortedList = _.orderBy(sortedList, [item => item.admin], ['desc']);
        break;
      case 'siteNumber':
        sortedList = _.orderBy(sortedList, [item => item.siteNumber], ['asc']);
        break;
      case '-siteNumber':
        sortedList = _.orderBy(sortedList, [item => item.siteNumber], ['desc']);
        break;
      case 'created':
        sortedList = _.orderBy(sortedList, [item => item.created], ['asc']);
        break;
      case '-created':
        sortedList = _.orderBy(sortedList, [item => item.created], ['desc']);
        break;
      default:
        sortedList = _.orderBy(sortedList, [item => item.email.toLowerCase()], ['asc']);
    }

    // apply pagination
    let start = pagination ? (pagination.page - 1) * pagination.per : 1;
    let end = pagination ? start + pagination.per : 0;
    paginatedList = _.slice(sortedList, start, end);
  }

  return {
    loggedInUser
    , paginatedList
    , siteStore: store.site
    , siteLocationStore: store.siteLocation
    , sortedList
    , sponsorStore: store.sponsor
    , studyStore: store.study
    , studyUserList
    , studyUserStore: store.studyUser
    , studyRoleStore: store.studyRole
    , userStore: store.user
    , userEventStore: store.userEvent
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminSingleSite)
);



// WEBPACK FOOTER //
// modules/site/views/AdminSingleSite.js.jsx